import React from 'react';
import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    padding: 20,
    fontSize: 12,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  infoBox: {
    width: 350,
    borderWidth: 2,
    borderColor: '#ccc',
    borderRadius: 10,
    padding: 10,
  },
  logo: {
    width: 50,
    height: 50,
    marginBottom: 5,
  },
  orderBox: {
    width: 200,
    borderWidth: 2,
    borderColor: '#ccc',
    borderRadius: 10,
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  section: {
    marginTop: 10,
    fontSize: 10,
  },
  table: {
    borderWidth: 2,
    borderColor: '#ccc',
    borderStyle: 'solid',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 2,
    borderBottomColor: '#ccc',
    paddingVertical: 3,
  },
  tableHeader: {
    backgroundColor: '#f0f0f0',
    fontWeight: 'bold',
  },
  cell: {
    padding: 5,
  },
  fractionContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 3,
  },
  fraction: {
    marginRight: 8,
    marginBottom: 2,
    alignItems: 'center',
  },
  fractionTop: {
    fontSize: 8,
    textAlign: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    paddingHorizontal: 3,
    paddingVertical: 1,
  },
  fractionBottom: {
    fontSize: 8,
    textAlign: 'center',
    paddingHorizontal: 3,
    paddingVertical: 1,
  },
  imgCell: {
    width: 60,
    alignItems: 'center',
    justifyContent: 'center',
  },
  refCell: { width: 100 },
  nameCell: { width: 250 },
  unitCell: { width: 150, textAlign: 'center' },
  totalCell: { width: 150, textAlign: 'center' },
  totalRow: {
    flexDirection: 'row',
    marginTop: 10,
  },
  totalLabel: {
    width: 420,
    padding: 5,
  },
  totalText: {
    width: 145,
    borderWidth: 2,
    borderColor: '#ccc',
    padding: 5,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  totalValue: {
    width: 150,
    borderWidth: 2,
    borderLeftWidth: 0,
    borderColor: '#ccc',
    padding: 5,
    textAlign: 'center',
    fontWeight: 'bold',
  },
});


const OrderPDF = ({ content }) => {

  if (!content || !Array.isArray(content.items)) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text style={{ color: 'red', fontSize: 14 }}>❌ Error: datos del PDF incompletos o corruptos.</Text>
        </Page>
      </Document>
    );
  }

  // Agrupar items por producto y color, igual que en OrderInfo.js
  const groupedItems = Object.values(
    content.items.reduce((acc, item) => {
      const key = `${item.nombre_producto}_${item.color || 'sin-color'}`;

      if (!acc[key]) {
        acc[key] = {
          ...item,
          variants: [],
          totalQuantity: 0,
          subtotal: 0,
        };
      }

      acc[key].variants.push({
        size: item.talla,
        quantity: item.cantidad,
      });

      acc[key].totalQuantity += item.cantidad;
      acc[key].subtotal += item.precio * item.cantidad;

      return acc;
    }, {})
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.infoBox}>
            <View style={{ alignItems: 'center', width: '100%' }}>
              {content.logo && (
                <Image
                  style={styles.logo}
                  src={content.logo}
                />
              )}
              <Text style={{ fontWeight: 'bold', fontSize: 12, marginTop: 5 }}>
                {content.nombre_empresa}
              </Text>
            </View>

            <Text style={{ marginTop: 5, fontSize: 10 }}>
              <Text style={{ fontWeight: 'bold' }}>Nombre del cliente: </Text>
              {content.nombre_cliente || 'No hay nombre'}
            </Text>
            <Text style={{ fontSize: 10 }}>
              <Text style={{ fontWeight: 'bold' }}>Número de celular:</Text> +57 {content.celular || 'Sin celular'}
            </Text>
            <Text style={{ fontWeight: 'bold', fontSize: 10 }}>Dirección:</Text>
            <Text style={{ fontSize: 10 }}>{content.direccion || 'No registra dirección'}</Text>
            <Text style={{ fontWeight: 'bold', fontSize: 10 }}>Notas:</Text>
            <Text style={{ fontSize: 10 }}>{content.notas || 'No registra notas'}</Text>
          </View>


          <View style={styles.orderBox}>
            <Text style={{ fontSize: 20, fontWeight: 'bold' }}>Pedido # {content.orden_num || '1001'}</Text>
            <Text>Fecha: {content.fecha || '01/01/2025'}</Text>
          </View>
        </View>

        <View style={styles.section}>
          <View style={[styles.table, styles.tableRow, styles.tableHeader]}>
            <Text style={[styles.cell, styles.refCell]}>Ref.</Text>
            <Text style={[styles.cell, styles.nameCell]}>Producto</Text>
            <Text style={[styles.cell, styles.unitCell]}>Valor unitario</Text>
            <Text style={[styles.cell, styles.totalCell]}>Valor total</Text>
          </View>

          {groupedItems?.map((groupedItem, index) => {
            const valorUnitario = Number(groupedItem.precio).toLocaleString('es-CO', { maximumFractionDigits: 0 });
            const totalItem = Number(groupedItem.subtotal).toLocaleString('es-CO');

            // Ordenar variantes por talla (igual que en OrderInfo.js)
            const sortedVariants = [...groupedItem.variants].sort((a, b) => {
              const sizeA = parseFloat(a.size) || 0;
              const sizeB = parseFloat(b.size) || 0;
              return sizeA - sizeB;
            });

            return (
              <View key={index} style={styles.tableRow}>
                <Text style={[styles.cell, styles.refCell]}>{groupedItem.referencia || ''}</Text>
                <View style={[styles.cell, styles.nameCell]}>
                  <Text>{groupedItem.nombre_producto || ''}</Text>
                  {groupedItem.color && <Text>Color: {groupedItem.color}</Text>}
                  <Text>x{groupedItem.totalQuantity}</Text>
                  <View style={styles.fractionContainer}>
                    {sortedVariants.map((variant, i) => (
                      <View key={i} style={styles.fraction}>
                        <Text style={styles.fractionTop}>{variant.size || '-'}</Text>
                        <Text style={styles.fractionBottom}>{variant.quantity}</Text>
                      </View>
                    ))}
                  </View>
                </View>
                <Text style={[styles.cell, styles.unitCell]}>{valorUnitario}</Text>
                <Text style={[styles.cell, styles.totalCell]}>{totalItem}</Text>
              </View>
            );
          })}

          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}></Text>
            <Text style={styles.totalText}>Total:</Text>
            <Text style={styles.totalValue}>
              {Number(content.total).toLocaleString('es-CO', { maximumFractionDigits: 0 }) || 0}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  )
};

export default OrderPDF;

